import { MOBILITY_STATISTICS_REDUCE_URL, SELF_SERVICE_STATISTICS_REDUCE_URL } from 'constants/ApiConstants';
import { MAX_RECORD } from 'constants/Global';
import {
  SELF_SERVICE_STATS_ORIGIN,
  SELF_SERVICE_STATS_REQUEST_TYPES,
  SELF_SERVICE_STATS_STATUS_BY_ORIGIN,
  SELF_SERVICE_STATS_HOUR_DISTRIBUTION_REDUCER,
  SELF_SERVICE_STATS_TIME_TO_COMPLETE_BY_ORIGIN,
} from 'constants/SelfServiceConstants';
import {
  WEEK,
  DATE_FIELD_API_PREFIX,
} from 'constants/StatisticsConstants';
import { GROUP_BY_API_SUFFIX } from 'modules/statistics/groupBy';
import { flatten } from 'utils/ArrayUtils';

/**
 * Returns the API reducer/sort config for a given dateField and groupBy granularity.
 * The API column name is derived from the dateField→prefix mapping and the granularity suffix.
 * Example: sendDate + week → sentOnYearWeek (sentOn is the API prefix for sendDate).
 *
 * @param {string | undefined} dateField - BO filter value ('createdOn' | 'sendDate' | 'startDate')
 * @param {string} groupBy - Granularity ('day' | 'week' | 'month' | 'quarter' | 'year')
 * @returns {{ reducer: string[], sort: string[] }}
 */
export const getGroupByFilters = (dateField, groupBy) => {
  const apiPrefix = DATE_FIELD_API_PREFIX[dateField] ?? DATE_FIELD_API_PREFIX.createdOn;
  const suffix = GROUP_BY_API_SUFFIX[groupBy];
  const col = `${apiPrefix}${suffix}`;

  return suffix ? { reducer: [`${col},none`], sort: [`${col},asc`] } : { reducer: [], sort: [] };
};

const formatCountByPeriodRequest = params => {
  const { groupBy = WEEK, origin: _, dateField, ...requestParams } = params;
  const { reducer: groupByReducer, sort } = getGroupByFilters(dateField, groupBy);

  return {
    method: 'GET',
    url: SELF_SERVICE_STATISTICS_REDUCE_URL,
    params: {
      ...requestParams,
      sort,
      size: MAX_RECORD,
      types: undefined,
      reducer: [
        'checkInSent,sum',
        'checkOutSent,sum',
        'homeCheckInCompleted,sum',
        'homeCheckOutCompleted,sum',
        'kioskCheckInCompleted,sum',
        'kioskCheckOutCompleted,sum',
        'manualCheckOutCompleted,sum',
        'onSiteOrKioskCheckInCompleted,sum',
        'onSiteOrKioskCheckOutCompleted,sum',
        ...groupByReducer,
      ],
    },
  };
};

const formatCountByStatusRequest = params => {
  const { groupBy: _, origin, types, ...requestParams } = params;

  const result = SELF_SERVICE_STATS_STATUS_BY_ORIGIN[origin][types].map(field => {
    const additionalParams = origin === SELF_SERVICE_STATS_ORIGIN.HOME ? {} : { exist: [field] };

    return {
      method: 'GET',
      url: SELF_SERVICE_STATISTICS_REDUCE_URL,
      params: {
        ...requestParams,
        types,
        size: MAX_RECORD,
        ...additionalParams,
        reducer: ['id,count', `${field},none`, ...(field !== 'manualCheckOutCompleted' ? ['dropOffStep,none'] : [])],
      },
    };
  });

  result.push({
    method: 'GET',
    url: SELF_SERVICE_STATISTICS_REDUCE_URL,
    params: {
      ...requestParams,
      types,
      size: MAX_RECORD,
      reducer: ['id,count'],
    },
  });

  return result;
};

const formatDetailedStatsRequest = params => {
  const { groupBy: _, origin, ...requestParams } = params;

  return {
    method: 'GET',
    url: SELF_SERVICE_STATISTICS_REDUCE_URL,
    params: {
      ...requestParams,
      reducer: [
        'id,count',
        'accepted,avg',
        'pdfDownloaded,avg',
        `${SELF_SERVICE_STATS_TIME_TO_COMPLETE_BY_ORIGIN[origin]},avg`,
        'onSiteOrKioskBreakdownCompleted,sum',
      ],
      exist: flatten([...(params.exist ? [params.exist] : []), 'answeredOn']),
    },
  };
};

const formatHourDistributionRequest = params => {
  const { groupBy: _, origin, ...requestParams } = params;
  return {
    method: 'GET',
    url: SELF_SERVICE_STATISTICS_REDUCE_URL,
    params: {
      ...requestParams,
      reducer: ['id,count', `${SELF_SERVICE_STATS_HOUR_DISTRIBUTION_REDUCER[origin]},none`],
      exist: flatten([...(params.exist ? [params.exist] : []), SELF_SERVICE_STATS_HOUR_DISTRIBUTION_REDUCER[origin]]),
    },
  };
};

const formatMobilityStatusRequest = params => {
  const { groupBy: _, origin: __, ...requestParams } = params;
  return {
    method: 'GET',
    url: MOBILITY_STATISTICS_REDUCE_URL,
    params: {
      ...requestParams,
      reducer: [
        'id,count',
        'sentOn,count,',
        'accepted,count',
        'refused,count',
        'contractSigned,count',
        // manually provided documents
        'manualDocumentsAccepted,sum',
        'manualDocumentsProvided,sum',
        'manualDocumentsRefused,sum',
        'manualDocumentsToValidate,sum',
        // automated provided documents
        'automaticDocumentsAutomaticallyAccepted,sum',
        'automaticDocumentsManuallyAccepted,sum',
        'automaticDocumentsManuallyRefused,sum',
        'automaticDocumentsProvided,sum',
        'automaticDocumentsToValidate,sum',
      ],
    },
  };
};

export const DETAILS_SELF_SERVICE_STATS_BY_TYPES = {
  [SELF_SERVICE_STATS_REQUEST_TYPES.COUNT_BY_PERIOD]: formatCountByPeriodRequest,
  [SELF_SERVICE_STATS_REQUEST_TYPES.COUNT_BY_STATUS]: formatCountByStatusRequest,
  [SELF_SERVICE_STATS_REQUEST_TYPES.DETAILED_STATS]: formatDetailedStatsRequest,
  [SELF_SERVICE_STATS_REQUEST_TYPES.HOUR_DISTRIBUTION]: formatHourDistributionRequest,
  [SELF_SERVICE_STATS_REQUEST_TYPES.MOBILITY_STATUS]: formatMobilityStatusRequest,
};
